import { createContext } from '@direct-frontend/modules-context';
import { type OpenApiSchemaManagerAuthType } from '../../api/schema';

export type ManagerPermissionName =
    | 'analytics'
    | 'content'
    | 'orders'
    | 'clients'
    | 'promotions'
    | 'store_promotion'
    | 'news'
    | 'editor'
    | 'settings'
    | 'closed_site_access';
export type ManagerPermissionAction = 'read' | 'write';
export type ManagerPermissions = Partial<Record<ManagerPermissionName, Record<ManagerPermissionAction, boolean>>>;
export type AuthMethod = OpenApiSchemaManagerAuthType;

export interface UserRequestContext {
    /** Почта текущего менеджера */
    email: string;
    /** Авторизован ли пользователь активным для кабинета способом */
    authed: boolean;
    /** Глобально включённый способ авторизации */
    activeAuthMethod: AuthMethod;
    /** Есть ли у текущего пользователя права доступа к текущему кабинету */
    b2bAccess: boolean;
    /** Признак владельца кабинета */
    isOwner: boolean;
    /** Признак суперадминистратора */
    isSuperAdmin: boolean;
    /** Признак внутреннего пользователя Яндекса */
    isInternalYandexUser: boolean;
    /** Внутренний пользователь по /manager, без поправки на dev-режим. undefined — данных ещё нет. */
    isInternalUser?: boolean;
    /** Признак того, что сайт находится в режиме обслуживания */
    isUnderMaintenance: boolean;
    /** Slug магазина из /manager */
    storeSlug?: string;
    /** Права менеджера из role.permissions (GET /manager) */
    permissions: ManagerPermissions;
    /**
     * HTTP-статус ответа бэкенда GET /manager.
     * Заполняется только при ошибке запроса, чтобы фронт мог проксировать
     * статус-код (например 5xx) и не маскировать падение бэка под 404/403.
     */
    backendStatusCode?: number;
}

/**
 * Дефолтное значение UserRequestContext для случаев, когда контекст недоступен
 * (нет данных от бэкенда или контекст ещё не установлен).
 */
export const DEFAULT_USER_REQUEST_CONTEXT: UserRequestContext = {
    email: '',
    authed: false,
    activeAuthMethod: 'yandex',
    b2bAccess: false,
    isOwner: false,
    isSuperAdmin: false,
    isInternalYandexUser: false,
    isUnderMaintenance: false,
    storeSlug: undefined,
    permissions: {},
};

/** Контекст, хранящий основную информацию о пользователе */
export const { setContext: setUserRequestContext, getContext: getUserRequestContext } =
    createContext<UserRequestContext>({
        name: 'userRequestContext',
    });

export const getSafeUserRequestContext: () => UserRequestContext = () => {
    try {
        return getUserRequestContext();
    } catch (_) {
        return DEFAULT_USER_REQUEST_CONTEXT;
    }
};
